<template>
  <header-block></header-block>
  <main class="seo-page">
    <section class="seo-hero">
      <div>
        <span class="eyebrow">{{ $t("editor.storefront_health") }}</span>
        <h1>{{ $t("seo.title") }}</h1>
        <p>
          {{ $t("seo.intro") }}
        </p>
      </div>
      <div class="device-toggle">
        <button
          :class="{ active: deviceStrategy === 'mobile' }"
          v-on:click="deviceStrategy = 'mobile'"
        >
          Phone
        </button>
        <button
          :class="{ active: deviceStrategy === 'desktop' }"
          v-on:click="deviceStrategy = 'desktop'"
        >
          Desktop
        </button>
      </div>
    </section>

    <section class="score-grid">
      <article class="score-card score-card-main">
        <div class="score-ring" :style="scoreStyle(overallScore)">
          <strong>{{ overallScore ?? "–" }}</strong
          ><span>/100</span>
        </div>
        <div>
          <span class="eyebrow">{{ $t("seo.overall") }}</span>
          <h2>{{ scoreLabel(overallScore) }}</h2>
          <p>{{ summary.pages_checked || 0 }} {{ $t("seo.checks") }}</p>
        </div>
      </article>
      <article class="metric-card metric-performance">
        <span>{{ $t("seo.performance") }}</span
        ><strong>{{ scoreFor("performance") }}</strong
        ><small>{{ $t("seo.performance_hint") }}</small>
      </article>
      <article class="metric-card metric-seo">
        <span>{{ $t("seo.seo_health") }}</span
        ><strong>{{ scoreFor("seo") }}</strong
        ><small>{{ $t("seo.search_readiness") }}</small>
      </article>
      <article class="metric-card metric-accessibility">
        <span>{{ $t("seo.accessibility") }}</span
        ><strong>{{ scoreFor("accessibility") }}</strong
        ><small>{{ $t("seo.inclusive") }}</small>
      </article>
      <article class="metric-card metric-vitals">
        <span>{{ $t("seo.vitals") }}</span
        ><strong>{{ vitalStatus }}</strong
        ><small>{{ lastChecked }}</small>
      </article>
    </section>

    <section
      class="panel field-panel"
      :class="{
        'field-panel-warning': fieldVitalsStatus === 'needs-attention',
      }"
    >
      <div class="panel-head">
        <div>
          <span class="eyebrow">{{ $t("seo.real_users") }}</span>
          <h2>{{ $t("seo.field") }}</h2>
        </div>
        <span class="muted">{{ $t("seo.chrome_data") }}</span>
      </div>
      <div class="field-grid" v-if="hasFieldData">
        <div
          :class="{
            'metric-warning': fieldMetricStatus('LCP') === 'needs-attention',
          }"
        >
          <strong>{{ fieldMetric("LCP", "ms") }}</strong>
          <span>LCP</span>
          <em v-if="fieldMetricStatus('LCP') === 'needs-attention'">{{
            $t("seo.attention")
          }}</em>
          <small v-else>{{
            $t("seo.better_pages", { percent: fieldMetricPercentile("LCP") })
          }}</small>
        </div>
        <div
          :class="{
            'metric-warning': fieldMetricStatus('INP') === 'needs-attention',
          }"
        >
          <strong>{{ fieldMetric("INP", "ms") }}</strong>
          <span>INP</span>
          <em v-if="fieldMetricStatus('INP') === 'needs-attention'">{{
            $t("seo.attention")
          }}</em>
          <small v-else>{{
            $t("seo.better_pages", { percent: fieldMetricPercentile("INP") })
          }}</small>
        </div>
        <div
          :class="{
            'metric-warning': fieldMetricStatus('CLS') === 'needs-attention',
          }"
        >
          <strong>{{ fieldMetric("CLS", "") }}</strong>
          <span>CLS</span>
          <em v-if="fieldMetricStatus('CLS') === 'needs-attention'">{{
            $t("seo.attention")
          }}</em>
          <small v-else>{{
            $t("seo.better_pages", { percent: fieldMetricPercentile("CLS") })
          }}</small>
        </div>
        <div
          :class="{
            'metric-warning': fieldMetricStatus('FCP') === 'needs-attention',
          }"
        >
          <strong>{{ fieldMetric("FCP", "ms") }}</strong>
          <span>FCP</span>
          <em v-if="fieldMetricStatus('FCP') === 'needs-attention'">{{
            $t("seo.attention")
          }}</em
          ><small v-else>{{
            $t("seo.better_pages", { percent: fieldMetricPercentile("FCP") })
          }}</small>
        </div>
      </div>
      <div
        class="benchmark"
        v-if="
          hasFieldData && latestFieldCheck?.vitals?.real_user_percentile > 50
        "
      >
        <strong
          >Better than
          {{ latestFieldCheck?.vitals?.real_user_percentile ?? "–" }}% of
          websites</strong
        >
        Based on industry used web performance percentile benchmark.
        <span v-if="fieldVitalsStatus === 'good'"
          >Your storefront currently meets the good-user-experience
          thresholds.</span
        >
        <span v-else-if="fieldVitalsStatus === 'needs-attention'"
          >Your storefront currently needs improvement against the
          good-user-experience thresholds.</span
        >
        <span v-else
          >Your storefront status will appear when all core user metrics are
          available.</span
        >
      </div>
      <div
        class="field-warning"
        v-else-if="fieldVitalsStatus === 'needs-attention'"
      >
        <strong>{{ $t("seo.attention") }}</strong>
        {{ $t("seo.needs") }}
      </div>
      <div class="search-empty" v-if="!hasFieldData">
        {{ $t("seo.no_field") }}
      </div>
    </section>

    <section class="panel search-panel">
      <div class="panel-head">
        <div>
          <span class="eyebrow">Search Console</span>
          <h2>{{ $t("seo.organic") }}</h2>
        </div>
        <span class="muted" v-if="search_console?.available">Last 28 days</span>
      </div>
      <div v-if="search_console?.available" class="search-stats">
        <div>
          <strong>{{ search_console.clicks.toLocaleString() }}</strong
          ><small>{{ $t("seo.clicks") }}</small>
        </div>
        <div>
          <strong>{{ search_console.impressions.toLocaleString() }}</strong
          ><small>{{ $t("seo.impressions") }}</small>
        </div>
        <div>
          <strong>{{ search_console.ctr }}%</strong
          ><small>{{ $t("seo.ctr") }}</small>
        </div>
        <div>
          <strong>{{ search_console.position ?? "–" }}</strong
          ><small>{{ $t("seo.position") }}</small>
        </div>
      </div>
      <div class="search-empty" v-else-if="search_console?.connected">
        {{ $t("seo.search_empty") }}
      </div>
      <div class="search-empty" v-else>
        {{ $t("seo.no_search") }}
      </div>
      <div
        class="query-list"
        v-if="search_console?.available && search_console.top_queries?.length"
      >
        <div
          class="query-row"
          v-for="item in search_console.top_queries"
          :key="item.query"
        >
          <span>{{ item.query }}</span
          ><small
            >{{ item.clicks }} {{ $t("seo.clicks").toLowerCase() }} ·
            {{ item.impressions }}
            {{ $t("seo.impressions").toLowerCase() }}</small
          >
        </div>
      </div>
    </section>

    <section class="dashboard-grid">
      <article class="panel trend-panel">
        <div class="panel-head">
          <div>
            <span class="eyebrow">Real measurements</span>
            <h2>{{ $t("seo.trend") }}</h2>
          </div>
          <span class="muted">{{ $t("seo.last_30") }}</span>
        </div>
        <div class="trend" v-if="trend.length">
          <div
            class="trend-bar"
            v-for="item in trend"
            :key="item.checked_at + item.strategy"
            :style="{ height: Math.max(item.performance_score || 4, 4) + '%' }"
            :title="item.performance_score + '% ' + item.strategy"
          ></div>
        </div>
        <div class="empty-state" v-else>
          {{ $t("seo.no_trend") }}
        </div>
      </article>
      <article class="panel">
        <div class="panel-head">
          <div>
            <span class="eyebrow">Attention needed</span>
            <h2>{{ $t("seo.opportunities") }}</h2>
          </div>
          <span class="issue-count">{{ issues.length }}</span>
        </div>
        <div class="issue-list">
          <div
            v-for="issue in issues.slice(0, 5)"
            :key="issue.id + issue.strategy"
            class="issue"
          >
            <span class="issue-dot"></span>
            <div>
              <strong>{{ issue.title }}</strong
              ><small>{{
                issue.displayValue || "Improvement recommended"
              }}</small>
            </div>
          </div>
          <div class="empty-state" v-if="!issues.length">
            {{ $t("seo.no_issues") }}
          </div>
          <button
            class="show-more"
            v-if="issues.length > 5"
            v-on:click="openIssueModal"
          >
            Show more
          </button>
        </div>
      </article>
    </section>
    <div
      class="modal-backdrop"
      v-if="issueModal"
      v-on:click.self="issueModal = false"
    >
      <section class="issue-modal">
        <div class="panel-head">
          <div>
            <span class="eyebrow">Attention needed</span>
            <h2>All opportunities</h2>
          </div>
          <button class="panel-close" v-on:click="issueModal = false">×</button>
        </div>
        <div class="issue-filters">
          <button
            v-for="filter in issueFilters"
            :key="filter.id"
            :class="{ active: issueFilter === filter.id }"
            v-on:click="issueFilter = filter.id"
          >
            {{ filter.label }}
          </button>
        </div>
        <div class="issue-detail-list">
          <div
            class="issue-detail"
            v-for="issue in filteredIssues()"
            :key="issue.id + issue.url + issue.strategy"
          >
            <strong>{{ issue.title }}</strong
            ><span>{{ issue.page_type }} · {{ issue.strategy }}</span
            ><small>{{ issue.displayValue || "Improvement recommended" }}</small
            ><a :href="issue.url" target="_blank" rel="noopener">{{
              issue.url
            }}</a>
          </div>
          <div class="empty-state" v-if="!filteredIssues().length">
            No opportunities in this section.
          </div>
        </div>
      </section>
    </div>

    <section class="panel webshop-panel">
      <div class="panel-head">
        <div>
          <span class="eyebrow">{{ $t("seo.storefronts") }}</span>
          <h2>{{ $t("seo.storefront_performance") }}</h2>
        </div>
      </div>
      <div class="shop-row" v-for="shop in webshops" :key="shop.id">
        <div>
          <strong>{{ shop.name }}</strong
          ><small>{{ shop.domain }}</small>
        </div>
        <span class="shop-score" :class="scoreClass(shopScore(shop.id))">{{
          shopScore(shop.id) ?? "–"
        }}</span
        ><button
          class="btn-outline"
          v-on:click="runCheck(shop.id)"
          :disabled="checking"
        >
          Check
        </button>
      </div>
      <div class="empty-state" v-if="!webshops.length">
        {{ $t("seo.no_shops") }}
      </div>
    </section>

    <section class="integration-grid">
      <article
        class="integration-card"
        v-for="integration in providers"
        :key="integration.id"
      >
        <div class="integration-icon" v-html="integration.logo"></div>
        <div>
          <h3>{{ integration.name }}</h3>
          <p>{{ integration.description }}</p>
        </div>
        <button
          class="btn-outline"
          :class="{ connected: integration.connected }"
          v-on:click="connect(integration.id)"
        >
          {{ integration.connected ? $t("seo.connected") : $t("seo.connect") }}
        </button>
      </article>
    </section>
  </main>
</template>

<script>
import HeaderBlock from "../components/header";
import http from "../modules/http";

const GOOGLE_LOGO =
  '<svg viewBox="0 0 24 24" width="21" height="21"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.3 9.14 5.38 12 5.38z"/></svg>';
const MICROSOFT_LOGO =
  '<svg viewBox="0 0 24 24" width="21" height="21"><path fill="#F25022" d="M3 3h8.5v8.5H3z"/><path fill="#7FBA00" d="M12.5 3H21v8.5h-8.5z"/><path fill="#00A4EF" d="M3 12.5h8.5V21H3z"/><path fill="#FFB900" d="M12.5 12.5H21V21h-8.5z"/></svg>';

export default {
  name: "SeoPage",
  components: { HeaderBlock },
  data() {
    return {
      summary: {},
      field_summary: {},
      search_console: {},
      checks: [],
      trend: [],
      issues: [],
      webshops: [],
      checking: false,
      deviceStrategy: "mobile",
      issueModal: false,
      issueFilter: "all",
      providers: [
        {
          id: "Google",
          name: "Google services",
          description: "Connect Search Console and Analytics.",
          logo: GOOGLE_LOGO,
        },
        {
          id: "Microsoft",
          name: "Microsoft services",
          description: "Connect Bing Webmaster tools.",
          logo: MICROSOFT_LOGO,
        },
      ],
    };
  },
  computed: {
    issueFilters() {
      return [
        { id: "all", label: "All" },
        { id: "home", label: "Home" },
        { id: "category", label: "Categories" },
        { id: "product", label: "Products" },
      ];
    },
    latest() {
      return (
        this.checks.find((check) => check.strategy === this.deviceStrategy) ||
        this.checks[0]
      );
    },
    overallScore() {
      return this.summary.overall_scores?.[this.deviceStrategy] ?? null;
    },
    latestFieldCheck() {
      return (
        this.checks.find(
          (check) =>
            check.strategy === this.deviceStrategy &&
            Object.values(check.vitals?.real_user || {}).some(
              (value) => value !== null && value !== undefined
            )
        ) || this.latest
      );
    },
    vitalStatus() {
      const vitals = this.latest?.vitals || {};
      return vitals.LCP && vitals.CLS !== null ? "Ready" : "–";
    },
    fieldVitals() {
      return Object.fromEntries(
        Object.entries(this.field_summary?.[this.deviceStrategy] || {}).map(
          ([key, value]) => [key, value.average]
        )
      );
    },
    hasFieldData() {
      return Object.values(this.fieldVitals).some(
        (value) => value !== null && value !== undefined
      );
    },
    fieldVitalsStatus() {
      const vitals = this.fieldVitals;
      if (vitals.LCP == null || vitals.INP == null || vitals.CLS == null)
        return "unknown";
      return vitals.LCP <= 2500 && vitals.INP <= 200 && vitals.CLS <= 0.1
        ? "good"
        : "needs-attention";
    },
    lastChecked() {
      return this.summary.last_checked_at
        ? "Checked " +
            new Date(this.summary.last_checked_at).toLocaleDateString()
        : "Not checked yet";
    },
  },
  mounted() {
    this.loadDashboard();
  },
  methods: {
    async loadDashboard() {
      try {
        const response = await http.fetch("/seo/dashboard");
        const data = response.data || {};
        Object.assign(this, data);
        const connected = data.integrations || {};
        this.providers = this.providers.map((provider) => ({
          ...provider,
          connected: Boolean(connected[provider.id]),
        }));
      } catch (e) {
        this.summary = {};
      }
    },
    scoreFor(type) {
      const keys = {
        performance: "performance_score",
        seo: "seo_score",
        accessibility: "accessibility_score",
        "best-practices": "best_practices_score",
      };
      const key = keys[type] || "seo_score";
      return this.latest?.[key] ?? "–";
    },
    shopScore(id) {
      return this.checks.find(
        (check) =>
          check.webshop_id === id && check.strategy === this.deviceStrategy
      )?.performance_score;
    },
    scoreStyle(score) {
      return {
        "--score": (score ?? 0) * 3.6 + "deg",
        "--score-color": this.scoreColor(score),
      };
    },
    scoreColor(score) {
      if (score == null) return "#c4b5fd";
      if (score > 90) return "#047857";
      if (score >= 80) return "#059669";
      if (score >= 70) return "#65a30d";
      if (score >= 50) return "#d97706";
      return "#dc2626";
    },
    scoreClass(score) {
      if (score == null) return "unknown";
      if (score > 90) return "excellent";
      if (score >= 80) return "good";
      if (score >= 70) return "fair";
      if (score >= 50) return "warn";
      return "bad";
    },
    scoreLabel(score) {
      return score == null
        ? this.$t("seo.waiting_score")
        : score > 90
        ? this.$t("seo.excellent")
        : score >= 80
        ? this.$t("seo.very_good")
        : score >= 70
        ? this.$t("seo.good")
        : score >= 50
        ? this.$t("seo.acceptable")
        : this.$t("seo.bad");
    },
    fieldMetric(key, unit) {
      const value = this.fieldVitals[key];
      return value === null || value === undefined ? "–" : value + unit;
    },
    fieldMetricStatus(key) {
      const value = this.fieldVitals[key];
      if (value === null || value === undefined) return "unknown";
      const thresholds = { LCP: 2500, INP: 200, CLS: 0.1, FCP: 1800 };
      return value <= thresholds[key] ? "good" : "needs-attention";
    },
    fieldMetricPercentile(key) {
      return this.latestFieldCheck?.vitals?.real_user_percentiles?.[key] ?? "–";
    },
    filteredIssues() {
      const issues = this.issues.map((issue) => ({
        ...issue,
        page_type: issue.page_type || this.pageTypeFromUrl(issue.url),
      }));
      return this.issueFilter === "all"
        ? issues
        : issues.filter((issue) => issue.page_type === this.issueFilter);
    },
    openIssueModal() {
      this.issueFilter = "all";
      this.issueModal = true;
    },
    pageTypeFromUrl(url) {
      const path = String(url || "");
      if (path.includes("/category/")) return "category";
      if (path.includes("/product/")) return "product";
      return "home";
    },
    async runCheck(webshopId) {
      this.checking = true;
      try {
        await http.fetch(
          "/seo/check",
          webshopId ? { webshop_id: webshopId } : {}
        );
        await this.loadDashboard();
      } finally {
        this.checking = false;
      }
    },
    async connect(type) {
      const response = await http.fetch("/service-integrations/login/" + type);
      if (response?.redirect) window.location.href = response.redirect;
    },
  },
};
</script>

<style scoped>
.seo-page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 34px 22px 60px;
  color: #172033;
}
.seo-hero {
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 20px;
  margin-bottom: 26px;
  padding: 28px 30px;
  border-radius: 20px;
  color: #fff;
  background: linear-gradient(120deg, #172554, #4c1d95 58%, #7c3aed);
  box-shadow: 0 16px 40px rgba(49, 46, 129, 0.18);
}
.device-toggle {
  display: inline-flex;
  margin-left: auto;
  padding: 3px;
  border-radius: 9px;
  background: rgba(255, 255, 255, 0.16);
}
.device-toggle button,
.issue-filters button {
  border: 0;
  border-radius: 7px;
  padding: 7px 11px;
  color: #ddd6fe;
  background: transparent;
  cursor: pointer;
  font-size: 11px;
  font-weight: 700;
}
.device-toggle button.active,
.issue-filters button.active {
  color: #312e81;
  background: #fff;
}
.seo-hero h1 {
  margin: 5px 0 8px;
  font-size: 30px;
  letter-spacing: -0.8px;
  color: #fff;
}
.seo-hero p {
  margin: 0;
  color: #ddd6fe;
  max-width: 560px;
}
.eyebrow {
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 10px;
  font-weight: 800;
  color: #8b5cf6;
}
.seo-hero .eyebrow {
  color: #c4b5fd;
}
.score-grid,
.dashboard-grid,
.integration-grid {
  display: grid;
  gap: 14px;
}
.score-grid {
  grid-template-columns: 2fr repeat(4, 1fr);
  margin-bottom: 14px;
}
.score-card,
.metric-card,
.panel,
.integration-card {
  background: #fff;
  border: 1px solid #e9e7f2;
  border-radius: 16px;
  box-shadow: 0 5px 18px rgba(30, 27, 75, 0.045);
}
.score-card {
  display: flex;
  align-items: center;
  gap: 22px;
  padding: 20px;
}
.score-ring {
  width: 94px;
  height: 94px;
  flex: none;
  display: grid;
  place-content: center;
  text-align: center;
  border-radius: 50%;
  background: conic-gradient(
    var(--score-color) var(--score),
    #ede9fe var(--score)
  );
  position: relative;
}
.score-ring:after {
  content: "";
  position: absolute;
  inset: 9px;
  border-radius: 50%;
  background: #fff;
}
.score-ring strong,
.score-ring span {
  position: relative;
  z-index: 1;
}
.score-ring strong {
  font-size: 27px;
}
.score-ring span {
  font-size: 10px;
  color: #94a3b8;
}
.score-card h2 {
  margin: 5px 0 4px;
  font-size: 18px;
}
.score-card p,
.metric-card small,
.integration-card p,
.shop-row small {
  color: #7c8498;
  font-size: 12px;
}
.metric-card {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.metric-card {
  border-top: 3px solid #c4b5fd;
}
.metric-performance {
  border-top-color: #7c3aed;
}
.metric-seo {
  border-top-color: #2563eb;
}
.metric-accessibility {
  border-top-color: #059669;
}
.metric-best-practices {
  border-top-color: #f59e0b;
}
.metric-vitals {
  border-top-color: #71760f;
}
.metric-card span {
  color: #697386;
  font-size: 12px;
  font-weight: 700;
}
.metric-card strong {
  font-size: 27px;
  color: #312e81;
}
.search-panel {
  margin-bottom: 14px;
}
.field-panel {
  margin-bottom: 14px;
}
.field-panel-warning {
  border-color: #f5c2c7;
  box-shadow: 0 5px 18px rgba(220, 38, 38, 0.08);
}
.field-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
.field-grid div {
  padding: 14px;
  border-radius: 10px;
  background: #f8fafc;
}
.field-grid .metric-warning {
  background: #fff1f2;
  outline: 1px solid #fecdd3;
}
.field-grid strong,
.field-grid small {
  display: block;
}
.field-grid em {
  display: block;
  margin-top: 5px;
  color: #b91c1c;
  font-size: 10px;
  font-style: normal;
  font-weight: 700;
}
.benchmark {
  margin-top: 14px;
  padding: 12px 14px;
  border-radius: 10px;
  color: #166534;
  background: #f0fdf4;
  font-size: 12px;
}
.field-warning {
  display: flex;
  gap: 6px;
  margin-top: 12px;
  padding: 11px 13px;
  border-radius: 9px;
  color: #991b1b;
  background: #fef2f2;
  font-size: 12px;
}
.field-grid strong {
  color: #0f766e;
  font-size: 20px;
}
.field-grid small {
  margin-top: 3px;
  color: #7c8498;
  font-size: 11px;
}
.search-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
.search-stats div {
  padding: 14px;
  border-radius: 10px;
  background: #f8f7ff;
}
.search-stats strong,
.search-stats small {
  display: block;
}
.search-stats strong {
  color: #312e81;
  font-size: 21px;
}
.search-stats small {
  margin-top: 3px;
  color: #7c8498;
  font-size: 11px;
}
.search-empty {
  padding: 12px 0 2px;
  color: #7c8498;
  font-size: 12px;
}
.query-list {
  margin-top: 14px;
  border-top: 1px solid #f0eff5;
}
.query-row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 0;
  border-bottom: 1px solid #f0eff5;
  font-size: 12px;
}
.query-row small {
  color: #8991a1;
}
.dashboard-grid {
  grid-template-columns: 1.5fr 1fr;
  margin-bottom: 14px;
}
.panel {
  padding: 20px;
}
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 10px;
  margin-bottom: 18px;
}
.panel h2 {
  margin: 5px 0 0;
  font-size: 17px;
}
.muted {
  font-size: 11px;
  color: #98a1b2;
}
.trend {
  height: 150px;
  display: flex;
  align-items: end;
  gap: 5px;
  padding: 10px 0;
  border-bottom: 1px solid #edeef4;
}
.trend-bar {
  flex: 1;
  min-width: 4px;
  border-radius: 5px 5px 0 0;
  background: linear-gradient(#8b5cf6, #c4b5fd);
}
.issue-count {
  background: #fef3c7;
  color: #b45309;
  border-radius: 20px;
  padding: 4px 9px;
  font-size: 12px;
  font-weight: 800;
}
.show-more {
  width: 100%;
  margin-top: 12px;
  border: 0;
  color: #5b21b6;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
}
.modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: grid;
  place-items: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.45);
}
.issue-modal {
  width: min(650px, 100%);
  max-height: 80vh;
  overflow: auto;
  padding: 22px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25);
}
.issue-filters {
  display: flex;
  gap: 5px;
  margin-bottom: 12px;
  padding: 4px;
  border-radius: 9px;
  background: #f1f5f9;
}
.issue-filters button {
  color: #64748b;
}
.issue-detail {
  display: grid;
  gap: 4px;
  padding: 12px 0;
  border-top: 1px solid #f0eff5;
}
.issue-detail strong {
  font-size: 13px;
}
.issue-detail span,
.issue-detail small,
.issue-detail a {
  color: #7c8498;
  font-size: 11px;
}
.issue-detail a {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #6d28d9;
}
.issue {
  display: flex;
  gap: 10px;
  padding: 11px 0;
  border-bottom: 1px solid #f0eff5;
}
.issue-dot {
  width: 7px;
  height: 7px;
  margin-top: 5px;
  flex: none;
  border-radius: 50%;
  background: #f59e0b;
}
.issue strong,
.shop-row strong {
  display: block;
  font-size: 13px;
}
.issue small {
  color: #8991a1;
  font-size: 11px;
}
.empty-state {
  color: #98a1b2;
  font-size: 12px;
  padding: 28px 0;
  text-align: center;
}
.shop-row {
  display: grid;
  grid-template-columns: 1fr 90px 70px;
  align-items: center;
  gap: 12px;
  padding: 13px 0;
  border-top: 1px solid #f0eff5;
}
.shop-score {
  font-weight: 800;
  font-size: 18px;
}
.good {
  color: #059669;
}
.excellent {
  color: #047857;
}
.fair {
  color: #65a30d;
}
.warn {
  color: #d97706;
}
.bad {
  color: #dc2626;
}
.btn-outline {
  border: 1px solid #ddd9ea;
  background: #fff;
  color: #4c1d95;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 12px;
  cursor: pointer;
}
.integration-grid {
  grid-template-columns: repeat(2, 1fr);
  margin-top: 14px;
}
.integration-card {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 17px;
}
.integration-icon {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 11px;
  background: #f5f3ff;
}
.integration-card h3 {
  margin: 0 0 3px;
  font-size: 14px;
}
.integration-card p {
  margin: 0;
}
.integration-card .btn-outline {
  margin-left: auto;
}
@media (max-width: 900px) {
  .score-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .score-card-main {
    grid-column: span 2;
  }
  .dashboard-grid {
    grid-template-columns: 1fr;
  }
  .search-stats {
    grid-template-columns: repeat(2, 1fr);
  }
  .field-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
@media (max-width: 560px) {
  .seo-page {
    padding: 20px 14px;
  }
  .seo-hero {
    display: block;
    padding: 22px;
  }
  .seo-hero button {
    margin-top: 18px;
  }
  .score-grid,
  .integration-grid {
    grid-template-columns: 1fr;
  }
  .score-card-main {
    grid-column: auto;
  }
  .shop-row {
    grid-template-columns: 1fr 50px 60px;
  }
}
</style>
